import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  BadgeCheck,
  Building2,
  CalendarClock,
  Check,
  ChevronDown,
  CircleCheck,
  ExternalLink,
  Pencil,
  Plus,
  ShieldCheck,
  Sparkles,
  UserPlus,
  X,
} from 'lucide-react';
import { Avatar, Button, Card, Field, Input, Pill, Select, Spinner } from '../../../components/ui';
import { organizationService } from '../../../services/organizationService';
import { ConfidenceBadge } from '../../intelligence/components/Confidence';
import {
  CHANGE_FIELD_LABEL,
  REORG_LABEL,
  UPDATE_LABEL,
  VERIFICATION_LABEL,
  VERIFICATION_SHORT,
  govDate,
  hostOf,
  plural,
  sinceLabel,
  verificationTone,
} from '../format';
import type { ApplyUpdateInput, ClosedRole, GovUpdateView, OrgRef } from '../types';
import { SourceKindTag } from './SourceKindTag';

/** The organization's own name, without the "(parent)" research wrote after it */
const baseName = (update: GovUpdateView): string => {
  const parent = update.organization.parent;
  const suffix = parent ? ` (${parent})` : '';
  return suffix && update.organization.label.endsWith(suffix)
    ? update.organization.label.slice(0, -suffix.length)
    : update.organization.label;
};

/**
 * Picks the organization when research named one that is not in the
 * directory — or creates it, which is usually the answer: a reshuffle makes
 * bodies the directory has never heard of.
 */
const OrganizationPicker: React.FC<{
  id: string;
  name: string;
  parent: string | null;
  creating: boolean;
  onChoose: (organizationId: string, label: string) => void;
  onCreate: () => void;
  onReset: () => void;
}> = ({ id, name, parent, creating, onChoose, onCreate, onReset }) => {
  const [term, setTerm] = useState('');
  const [results, setResults] = useState<Array<{ id: string; name: string; type: string }>>([]);
  const [chosen, setChosen] = useState<string | null>(null);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    if (term.trim().length < 2) {
      setResults([]);
      return;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await organizationService.getOrganizations({ search: term.trim(), limit: 6 });
        if (!cancelled) setResults(data.map((org) => ({ id: org.id, name: org.name, type: org.type })));
      } catch {
        if (!cancelled) setResults([]);
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [term]);

  const picked = chosen ?? (creating ? `New record: ${name}` : null);

  return (
    <Field label="Which organization?" htmlFor={id} hint={picked ? undefined : 'Not in the directory yet'}>
      {picked ? (
        <p className="flex items-center gap-2 text-[13px] text-ink">
          {creating ? (
            <Plus className="h-3.5 w-3.5 flex-shrink-0 text-emerald-600" strokeWidth={2} />
          ) : (
            <Check className="h-3.5 w-3.5 flex-shrink-0 text-emerald-600" strokeWidth={2} />
          )}
          <span className="min-w-0 truncate">{picked}</span>
          <button
            type="button"
            onClick={() => {
              setChosen(null);
              onReset();
            }}
            className="flex-shrink-0 text-[12px] text-neutral-500 underline decoration-neutral-300 underline-offset-2 hover:text-ink"
          >
            Change
          </button>
        </p>
      ) : (
        <>
          <button
            type="button"
            onClick={onCreate}
            className="mb-1.5 flex w-full items-start gap-2 rounded-xl border border-dashed border-neutral-300 px-3 py-2 text-left text-[12.5px] transition-colors hover:border-neutral-400 hover:bg-neutral-50"
          >
            <Plus className="mt-px h-3.5 w-3.5 flex-shrink-0 text-neutral-500" strokeWidth={2} />
            <span className="min-w-0">
              <span className="block text-ink">Create “{name}”</span>
              <span className="block text-[11.5px] text-neutral-400">
                {parent ? `Under ${parent}, if the directory has it` : 'As a new organization'}
              </span>
            </span>
          </button>
          <div className="relative">
            <Input
              id={id}
              value={term}
              onChange={(event) => setTerm(event.target.value)}
              placeholder="Or find it under another name"
            />
            {searching && <Spinner className="absolute right-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2" />}
          </div>
          {results.length > 0 && (
            <ul className="mt-1.5 space-y-0.5">
              {results.map((result) => (
                <li key={result.id}>
                  <button
                    type="button"
                    onClick={() => {
                      onChoose(result.id, result.name);
                      setChosen(result.name);
                      setResults([]);
                      setTerm('');
                    }}
                    className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-[12.5px] transition-colors hover:bg-neutral-100"
                  >
                    <span className="min-w-0 flex-1 truncate text-ink">{result.name}</span>
                    <span className="flex-shrink-0 text-[11.5px] text-neutral-400">{result.type}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </Field>
  );
};

/** Colour says what the change is about: a person's post, or the body itself */
const KIND_TONE: Record<GovUpdateView['kind'], 'teal' | 'amber' | 'blue' | 'violet'> = {
  appointment: 'teal',
  departure: 'amber',
  correction: 'blue',
  rename: 'violet',
  restructure: 'violet',
  duplicate: 'violet',
  move: 'violet',
  former: 'teal',
};

const OrgLink: React.FC<{ org: OrgRef; strike?: boolean }> = ({ org, strike }) =>
  org.organizationId ? (
    <Link
      to={`/organizations/${org.organizationId}`}
      className={`min-w-0 truncate text-ink hover:underline hover:decoration-neutral-300 hover:underline-offset-2 ${
        strike ? 'text-neutral-500 line-through decoration-neutral-300' : ''
      }`}
    >
      {org.label}
    </Link>
  ) : (
    <span className="min-w-0 truncate text-ink">{org.label}</span>
  );

/** Which bodies stop, and which take over — the before and after of a reshuffle */
const Reorganisation: React.FC<{ update: GovUpdateView; applied: boolean }> = ({ update, applied }) => {
  const reorganisation = update.reorganisation;
  if (!reorganisation) return null;
  return (
    <div className="grid grid-cols-1 gap-2 rounded-xl bg-neutral-50 px-3 py-2.5 text-[12.5px] sm:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] sm:items-center">
      <div className="min-w-0 space-y-1">
        <p className="text-[11.5px] uppercase tracking-[0.06em] text-neutral-400">
          {reorganisation.how === 'merge' ? 'These end' : 'This ends'}
        </p>
        {reorganisation.from.map((org) => (
          <p key={org.nodeId ?? org.label} className="flex min-w-0 items-center gap-1.5">
            <Building2 className="h-3.5 w-3.5 flex-shrink-0 text-neutral-400" strokeWidth={1.75} />
            <OrgLink org={org} strike />
          </p>
        ))}
      </div>
      <span className="flex items-center gap-1 text-[11.5px] text-neutral-400 sm:flex-col">
        <ArrowRight className="h-3.5 w-3.5 rotate-90 sm:rotate-0" strokeWidth={1.75} />
        {REORG_LABEL[reorganisation.how]}
      </span>
      <div className="min-w-0 space-y-1">
        <p className="text-[11.5px] uppercase tracking-[0.06em] text-neutral-400">Takes over</p>
        {reorganisation.into.map((org) => (
          <p key={org.nodeId ?? org.label} className="flex min-w-0 items-center gap-1.5">
            {org.organizationId ? (
              <Building2 className="h-3.5 w-3.5 flex-shrink-0 text-teal-600" strokeWidth={1.75} />
            ) : (
              <Plus className="h-3.5 w-3.5 flex-shrink-0 text-emerald-600" strokeWidth={2} />
            )}
            <OrgLink org={org} />
            {!org.organizationId && !applied && (
              <span className="flex-shrink-0 text-[11.5px] text-emerald-700">new record</span>
            )}
          </p>
        ))}
      </div>
    </div>
  );
};

/**
 * Entries of the map that are one body, and which one stays. Before it is
 * applied the reviewer can keep another one — the model's choice is a
 * proposal like the rest.
 */
const Duplicate: React.FC<{
  update: GovUpdateView;
  keepId: string;
  onKeep?: (nodeId: string) => void;
}> = ({ update, keepId, onKeep }) => {
  const duplicate = update.duplicate;
  if (!duplicate) return null;
  const pending = update.status === 'pending';
  const entries = [duplicate.keep, ...duplicate.fold];
  const bodies = entries.filter((entry) => !entry.post);
  return (
    <div className="space-y-2 rounded-xl bg-neutral-50 px-3 py-2.5 text-[12.5px]">
      <p className="text-[11.5px] uppercase tracking-[0.06em] text-neutral-400">
        {pending ? (bodies.length > 1 ? 'One body — keep' : 'Folds into') : 'Merged'}
      </p>
      <ul className="space-y-1.5">
        {entries.map((entry) => {
          const kept = entry.nodeId === keepId;
          const choosable = pending && !!onKeep && !entry.post && bodies.length > 1;
          return (
            <li key={entry.nodeId}>
              <label className={`flex items-start gap-2 ${choosable ? 'cursor-pointer' : ''}`}>
                {choosable ? (
                  <input
                    type="radio"
                    name={`keep-${update.id}`}
                    checked={kept}
                    onChange={() => onKeep?.(entry.nodeId)}
                    className="mt-0.5 h-3.5 w-3.5 border-neutral-300 text-primary-600 focus:ring-primary-600/40"
                  />
                ) : (
                  <Building2
                    className={`mt-px h-3.5 w-3.5 flex-shrink-0 ${kept ? 'text-teal-600' : 'text-neutral-400'}`}
                    strokeWidth={1.75}
                  />
                )}
                <span className="min-w-0">
                  <span className="flex flex-wrap items-center gap-1.5">
                    <span
                      className={
                        kept || !pending ? 'text-ink' : 'text-neutral-500 line-through decoration-neutral-300'
                      }
                    >
                      {entry.name}
                    </span>
                    {entry.post && <Pill tone="outline">A post, not a body</Pill>}
                    {kept && pending && <span className="text-[11.5px] text-teal-700">stays</span>}
                  </span>
                  <span className="block text-[11.5px] text-neutral-400">
                    {entry.parent ? `Under ${entry.parent}` : 'Top level'} ·{' '}
                    {entry.record
                      ? `record “${entry.record}”, ${plural(entry.people, 'role')}`
                      : 'no record yet'}
                  </span>
                </span>
              </label>
            </li>
          );
        })}
      </ul>
      {pending && duplicate.name && (
        <p className="text-[12px] text-neutral-500">
          Official name, per the acts: <span className="text-ink">{duplicate.name}</span>
        </p>
      )}
      {pending && (
        <p className="text-[11.5px] leading-snug text-neutral-400">
          Merging folds the other entries into the one that stays, and their records into its record —
          roles, relationships and history move with them.
        </p>
      )}
    </div>
  );
};

/** An act that kept someone in post, which is not when they took it */
const Confirmed: React.FC<{ update: GovUpdateView }> = ({ update }) => {
  const confirmation = update.confirmation;
  if (!confirmation) return null;
  return (
    <p className="flex items-start gap-1.5 rounded-xl bg-sky-50 px-3 py-2 text-[12px] leading-snug text-sky-800">
      <BadgeCheck className="mt-px h-3.5 w-3.5 flex-shrink-0" strokeWidth={1.75} />
      <span>
        Confirmed in a post they already held
        {confirmation.act ? ` by ${confirmation.act}` : ''}
        {confirmation.date ? ` on ${govDate(confirmation.date)}` : ''}. That is not when they took it
        {update.effectiveDate ? ` — they have held it since ${govDate(update.effectiveDate)}` : ''}.
      </span>
    </p>
  );
};

/** The first thing the Applied card says: what actually changed in the directory */
const Receipt: React.FC<{ update: GovUpdateView }> = ({ update }) => {
  const done = (update.applied ?? {}) as Record<string, any>;
  const lines: React.ReactNode[] = [];
  const person = (id: string | null | undefined, label: string) =>
    id ? (
      <Link to={`/stakeholders/${id}`} className="text-ink underline decoration-neutral-300 underline-offset-2">
        {label}
      </Link>
    ) : (
      <span className="text-ink">{label}</span>
    );
  const organization = (id: string | null | undefined, label: string) =>
    id ? (
      <Link to={`/organizations/${id}`} className="text-ink underline decoration-neutral-300 underline-offset-2">
        {label}
      </Link>
    ) : (
      <span className="text-ink">{label}</span>
    );

  const created = [
    ...(done.createdOrganization ? [done.createdOrganization] : []),
    ...((done.createdOrganizations as Array<{ id: string; label: string }> | undefined) ?? []),
  ] as Array<{ id: string; label: string }>;
  created.forEach((org) =>
    lines.push(
      <>
        Created {organization(org.id, org.label)}
      </>,
    ),
  );

  if (update.kind === 'appointment' && done.stakeholderId) {
    lines.push(
      <>
        {person(done.stakeholderId, done.personLabel || update.person?.label || 'The person')}
        {done.createdPerson ? ' (new to the directory)' : ''} {done.alreadyRecorded ? 'was already' : 'is now'}{' '}
        {done.role || update.roleTitle}
        {done.since ? `, from ${govDate(done.since)}` : ''}
        {done.undated ? ' — no date was known, so today was used' : ''}
      </>,
    );
  }
  if (update.kind === 'former' && done.stakeholderId) {
    lines.push(
      <>
        {person(done.stakeholderId, done.personLabel || update.person?.label || 'The person')}
        {done.createdPerson ? ' (new to the directory)' : ''} {done.alreadyRecorded ? 'was already recorded as' : 'is recorded as'}{' '}
        former {done.role || update.roleTitle}
        {done.since && done.until ? `, ${govDate(done.since)} – ${govDate(done.until)}` : ''}
      </>,
    );
  }
  if (update.kind === 'correction' && done.after) {
    lines.push(
      <>
        {person(done.stakeholderId, done.personLabel || update.person?.label || 'The role')}: in post since{' '}
        {govDate(done.after.startDate)}
        {done.before?.startDate ? `, no longer ${govDate(done.before.startDate)}` : ''}
      </>,
    );
  }
  if (update.kind === 'rename' && done.name) {
    lines.push(
      <>
        {organization(done.organizationId, done.name)}, formerly “{done.previous}”, with the change in its name
        history
      </>,
    );
  }
  if (update.kind === 'duplicate') {
    const folded =
      (done.folded as string[] | undefined) ?? update.duplicate?.fold.map((entry) => entry.name) ?? [];
    const kept = (done.kept as { name?: string } | undefined)?.name ?? update.duplicate?.keep.name ?? '';
    lines.push(
      <>
        {folded.map((name) => `“${name}”`).join(', ')} folded into{' '}
        {organization(done.organizationId, kept)}
      </>,
    );
    ((done.mergedRecords as Array<{ id: string; label: string }> | undefined) ?? []).forEach((record) =>
      lines.push(<>The record “{record.label}” was merged into it</>),
    );
    if (done.movedPeople) lines.push(<>{plural(done.movedPeople, 'role')} moved with them</>);
    if (done.collapsedRoles)
      lines.push(<>{plural(done.collapsedRoles, 'role')} recorded twice for one person kept once</>);
    if ((done.heldBack as string[] | undefined)?.length)
      lines.push(<>Held-back records were left as they are</>);
  }
  if (update.kind === 'restructure' && update.reorganisation) {
    lines.push(
      <>
        {update.reorganisation.from.map((org) => org.label).join(', ')} marked as{' '}
        {REORG_LABEL[update.reorganisation.how].toLowerCase()} in the map; the record keeps its history
      </>,
    );
  }
  ((done.closed as Array<ClosedRole | { id: string; role: string }> | undefined) ?? []).forEach((role) => {
    const closed = role as Partial<ClosedRole>;
    lines.push(
      <>
        {closed.personLabel ? person(closed.stakeholderId, closed.personLabel) : 'A role'}
        {closed.personLabel ? '’s' : ''} role as {role.role} ended
        {closed.endDate ? ` on ${govDate(closed.endDate)}` : ''}
      </>,
    );
  });

  return (
    <div className="space-y-1.5 rounded-xl bg-emerald-50/60 px-3 py-2.5 text-[12.5px] text-neutral-600">
      <p className="flex items-center gap-1.5 text-[11.5px] uppercase tracking-[0.06em] text-emerald-700">
        <CircleCheck className="h-3.5 w-3.5" strokeWidth={2} />
        Done{update.reviewedAt ? ` · ${sinceLabel(update.reviewedAt)}` : ''}
      </p>
      {lines.length ? (
        <ul className="space-y-1">
          {lines.map((line, index) => (
            <li key={index} className="flex gap-1.5 leading-snug">
              <span className="mt-[7px] h-1 w-1 flex-shrink-0 rounded-full bg-emerald-600" />
              <span className="min-w-0">{line}</span>
            </li>
          ))}
        </ul>
      ) : (
        <p>Written to the directory.</p>
      )}
    </div>
  );
};

/**
 * One proposed change, with the evidence behind it and the three things a
 * reviewer can do: apply it, change it first, or say no.
 *
 * The before and after are both on the card. Applying an appointment ends
 * somebody else's role, and that has to be visible before it happens — with
 * the day it ends — not discovered afterwards. Once applied, the card stays
 * where it was and says what was done, with links to what it touched.
 */
export const UpdateCard: React.FC<{
  update: GovUpdateView;
  canEdit: boolean;
  busy?: boolean;
  onApply: (input: ApplyUpdateInput) => void;
  onDismiss: (reason?: string) => void;
  onVerify?: () => void;
  compact?: boolean;
}> = ({ update, canEdit, busy, onApply, onDismiss, onVerify, compact }) => {
  const [editing, setEditing] = useState(false);
  const [showEvidence, setShowEvidence] = useState(false);
  const [draft, setDraft] = useState<ApplyUpdateInput>({});
  const [closing, setClosing] = useState<string[]>(update.closes.map((role) => role.assignmentId));
  const [keepId, setKeepId] = useState(update.duplicate?.keep.nodeId ?? '');

  const pending = update.status === 'pending';
  const person = update.person;
  const evidence = update.evidence ?? [];

  // An appointment at a body research could not place needs a record: one
  // somebody picks, or one made for it
  const needsOrganization =
    update.kind === 'appointment' && !update.organization.id && !draft.organizationId && !draft.createOrganization;
  // Roles end on the day the change took effect; without one, today
  const effective = draft.effectiveDate || update.effectiveDate;
  const undated =
    pending && !effective && ['appointment', 'departure', 'restructure'].includes(update.kind);
  // An act that only confirmed someone is not their start, and today is not
  // either: the day they took the post has to be known
  const needsDate = pending && update.kind === 'appointment' && !!update.confirmation && !effective;
  const duplicate = update.kind === 'duplicate';

  const apply = () => {
    if (needsOrganization || needsDate) {
      setEditing(true);
      return;
    }
    onApply({
      ...draft,
      closeAssignmentIds: closing,
      ...(duplicate ? { keepNodeId: keepId } : {}),
    });
  };

  return (
    <Card className={`overflow-hidden ${compact ? 'text-[12.5px]' : ''}`}>
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 border-b border-neutral-200/70 px-4 py-2.5">
        <Pill tone={KIND_TONE[update.kind]}>
          {update.kind === 'restructure' && update.reorganisation
            ? REORG_LABEL[update.reorganisation.how]
            : UPDATE_LABEL[update.kind]}
        </Pill>
        <span className="min-w-0 flex-1 truncate text-[12.5px] text-neutral-500">
          {update.organization.id ? (
            <Link
              to={`/organizations/${update.organization.id}`}
              className="hover:text-ink hover:underline hover:decoration-neutral-300 hover:underline-offset-2"
            >
              {update.organization.label}
            </Link>
          ) : (
            <span title="This organization is not in the directory yet">
              {baseName(update)}
              {update.organization.parent && (
                <span className="text-neutral-400"> · under {update.organization.parent}</span>
              )}
            </span>
          )}
        </span>
        {!update.organization.id && pending && update.kind === 'appointment' && (
          <Pill tone={draft.createOrganization || draft.organizationId ? 'green' : 'amber'}>
            {draft.createOrganization ? 'Will be created' : draft.organizationId ? 'Chosen' : 'Not in the directory'}
          </Pill>
        )}
        {update.effectiveDate && (
          <span className="flex-shrink-0 text-[12px] tabular-nums text-neutral-400">
            {govDate(update.effectiveDate)}
          </span>
        )}
        {update.status !== 'pending' && (
          <Pill tone={update.status === 'applied' ? 'green' : 'neutral'}>
            {update.status === 'applied' ? 'Applied' : update.status === 'dismissed' ? 'Dismissed' : 'Superseded'}
          </Pill>
        )}
      </div>

      <div className="space-y-3 px-4 py-3">
        {update.status === 'applied' && <Receipt update={update} />}

        {/* ---- who and what ---- */}
        {person && (
          <div className="flex items-start gap-2.5">
            <Avatar name={person.label} kind="stakeholder" size="sm" />
            <div className="min-w-0 flex-1">
              <p className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
                {person.id ? (
                  <Link
                    to={`/stakeholders/${person.id}`}
                    className="truncate text-[13px] text-ink hover:underline hover:decoration-neutral-300 hover:underline-offset-2"
                  >
                    {person.label}
                  </Link>
                ) : (
                  <span className="truncate text-[13px] text-ink">{person.label}</span>
                )}
                {person.isNew && pending && (
                  <Pill tone="outline">
                    <UserPlus className="mr-1 h-3 w-3" strokeWidth={1.75} />
                    New person
                  </Pill>
                )}
              </p>
              {update.roleTitle && <p className="mt-0.5 text-[12.5px] text-neutral-600">{update.roleTitle}</p>}
            </div>
          </div>
        )}

        <p className="text-[12.5px] leading-snug text-neutral-600">{update.summary}</p>
        {/* The post was at a body that no longer exists, not yet on file */}
        {(update.kind === 'former' || update.kind === 'correction') &&
          update.reorganisation &&
          !update.organization.id &&
          update.status === 'pending' && (
            <p className="flex flex-wrap items-center gap-1.5 text-[12px] leading-snug text-neutral-500">
              <span className="rounded-full bg-neutral-200/70 px-1.5 py-px text-[9.5px] uppercase tracking-[0.05em] text-neutral-500">
                Defunct
              </span>
              Applying records {update.organization.label} as a body that no longer exists, linked to{' '}
              {update.reorganisation.into.map((ref) => ref.label).join(' and ')}.
            </p>
          )}

        {update.kind === 'restructure' && <Reorganisation update={update} applied={!pending} />}

        {duplicate && <Duplicate update={update} keepId={keepId} onKeep={canEdit ? setKeepId : undefined} />}

        {update.kind === 'appointment' && <Confirmed update={update} />}

        {/* ---- what it would change ---- */}
        {pending && update.closes.length > 0 && (
          <div className="space-y-1.5 rounded-xl bg-neutral-50 px-3 py-2.5">
            <p className="text-[11.5px] uppercase tracking-[0.06em] text-neutral-400">
              {update.kind === 'departure' ? 'Would end' : 'Would also end'}
              <span className="normal-case tracking-normal">
                {' '}
                · on {effective ? govDate(effective) : 'today'}
              </span>
            </p>
            {update.closes.map((role) => {
              const checked = closing.includes(role.assignmentId);
              return (
                <label key={role.assignmentId} className="flex cursor-pointer items-start gap-2 text-[12.5px]">
                  {canEdit && update.closes.length > 1 && (
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() =>
                        setClosing((current) =>
                          current.includes(role.assignmentId)
                            ? current.filter((id) => id !== role.assignmentId)
                            : [...current, role.assignmentId],
                        )
                      }
                      className="mt-0.5 h-3.5 w-3.5 rounded border-neutral-300 text-primary-600 focus:ring-primary-600/40"
                    />
                  )}
                  <span className={`min-w-0 ${checked ? '' : 'opacity-50'}`}>
                    <Link
                      to={`/stakeholders/${role.stakeholderId}`}
                      className="text-ink hover:underline hover:decoration-neutral-300 hover:underline-offset-2"
                    >
                      {role.personLabel}
                    </Link>
                    <span className="text-neutral-500">
                      {' — '}
                      <span className={checked ? 'line-through decoration-neutral-400' : ''}>{role.role}</span>
                      {role.startDate ? ` · ${govDate(role.startDate)}` : ''}
                      {checked ? ` → ${effective ? govDate(effective) : 'today'}` : ' · stays'}
                    </span>
                  </span>
                </label>
              );
            })}
          </div>
        )}

        {update.changes.some((change) => change.field !== 'parentRecord') && (
          <div className="space-y-1 rounded-xl bg-neutral-50 px-3 py-2.5 text-[12.5px]">
            {update.changes
              .filter((change) => change.field !== 'parentRecord')
              .map((change) => (
              <p key={change.field} className="flex flex-wrap items-center gap-1.5">
                <span className="text-neutral-400">{CHANGE_FIELD_LABEL[change.field] ?? change.field}</span>
                <span className="text-neutral-500 line-through">
                  {change.field === 'startDate' ? govDate(change.from) : change.from || '—'}
                </span>
                <ArrowRight className="h-3 w-3 text-neutral-400" strokeWidth={1.75} />
                <span className="text-ink">{change.field === 'startDate' ? govDate(change.to) : change.to}</span>
              </p>
            ))}
          </div>
        )}

        {undated && canEdit && (
          <p className="flex items-start gap-1.5 rounded-xl bg-amber-50 px-3 py-2 text-[12px] text-amber-800">
            <CalendarClock className="mt-px h-3.5 w-3.5 flex-shrink-0" strokeWidth={1.75} />
            <span>
              {needsDate
                ? 'When they first took the post is not known — set it before applying.'
                : 'No date found — roles would start and end today.'}{' '}
              <button
                type="button"
                onClick={() => setEditing(true)}
                className="underline decoration-amber-300 underline-offset-2 hover:text-amber-900"
              >
                Set the date
              </button>
            </span>
          </p>
        )}

        {/* ---- how sure ---- */}
        <div className="flex flex-wrap items-center gap-2">
          <ConfidenceBadge value={update.confidence} />
          <Pill tone={verificationTone(update.verification)} title={VERIFICATION_LABEL[update.verification]}>
            {VERIFICATION_SHORT[update.verification]}
          </Pill>
          {evidence.length > 0 && (
            <button
              type="button"
              onClick={() => setShowEvidence((value) => !value)}
              className="inline-flex items-center gap-1 text-[12px] text-neutral-500 transition-colors hover:text-ink"
              aria-expanded={showEvidence}
            >
              {evidence.length === 1 ? '1 source' : `${evidence.length} sources`}
              <ChevronDown
                className={`h-3.5 w-3.5 transition-transform ${showEvidence ? 'rotate-180' : ''}`}
                strokeWidth={1.75}
              />
            </button>
          )}
          <span className="ml-auto text-[11.5px] text-neutral-400">{sinceLabel(update.createdAt)}</span>
        </div>

        {showEvidence && (
          <ol className="space-y-2 border-t border-neutral-100 pt-2.5">
            {evidence.map((source, index) => (
              <li key={source.url} className="flex gap-2 text-[12px] leading-snug">
                <span className="mt-px inline-flex h-4 w-4 flex-shrink-0 items-center justify-center rounded bg-neutral-100 text-[10px] tabular-nums text-neutral-500">
                  {index + 1}
                </span>
                <span className="min-w-0">
                  {source.claim && <span className="text-neutral-700">{source.claim}</span>}
                  <span className="mt-0.5 flex flex-wrap items-center gap-x-1.5 text-[11.5px] text-neutral-400">
                    <a
                      href={source.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex min-w-0 items-center gap-1 truncate text-neutral-500 underline decoration-neutral-300 underline-offset-2 hover:text-ink"
                    >
                      {source.publisher || hostOf(source.url)}
                      <ExternalLink className="h-3 w-3 flex-shrink-0" strokeWidth={1.75} />
                    </a>
                    {source.date && <span>· {govDate(source.date)}</span>}
                    <SourceKindTag kind={source.kind} />
                  </span>
                </span>
              </li>
            ))}
          </ol>
        )}

        {/* ---- changing it before applying ---- */}
        {editing && pending && (
          <div className="space-y-3 rounded-xl border border-neutral-200 p-3">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {['appointment', 'correction', 'former'].includes(update.kind) && (
                <Field label="Title" htmlFor={`title-${update.id}`}>
                  <Input
                    id={`title-${update.id}`}
                    value={draft.roleTitle ?? update.roleTitle ?? ''}
                    onChange={(event) => setDraft({ ...draft, roleTitle: event.target.value })}
                  />
                </Field>
              )}
              <Field
                label={update.confirmation ? 'In post since' : update.kind === 'former' ? 'Took the post' : 'Took effect'}
                htmlFor={`date-${update.id}`}
                hint="YYYY-MM-DD"
              >
                <Input
                  id={`date-${update.id}`}
                  value={draft.effectiveDate ?? update.effectiveDate ?? ''}
                  onChange={(event) => setDraft({ ...draft, effectiveDate: event.target.value })}
                  placeholder="2026-03-12"
                />
              </Field>
            </div>
            {update.kind === 'appointment' && !update.organization.id && (
              <OrganizationPicker
                id={`org-${update.id}`}
                name={baseName(update)}
                parent={update.organization.parent}
                creating={!!draft.createOrganization}
                onChoose={(organizationId) =>
                  setDraft({ ...draft, organizationId, createOrganization: false })
                }
                onCreate={() => setDraft({ ...draft, organizationId: null, createOrganization: true })}
                onReset={() => setDraft({ ...draft, organizationId: null, createOrganization: false })}
              />
            )}
            {person && (person.candidates.length > 0 || !person.id) && (
              <Field label="Who is this?" htmlFor={`person-${update.id}`}>
                <Select
                  id={`person-${update.id}`}
                  value={draft.createPerson ? 'new' : (draft.stakeholderId ?? person.id ?? 'new')}
                  onChange={(event) =>
                    setDraft({
                      ...draft,
                      createPerson: event.target.value === 'new',
                      stakeholderId: event.target.value === 'new' ? null : event.target.value,
                    })
                  }
                >
                  {person.id && <option value={person.id}>{person.label} (matched)</option>}
                  {person.candidates.map((candidate) => (
                    <option key={candidate.id} value={candidate.id}>
                      {candidate.label} ({Math.round(candidate.score * 100)}%)
                    </option>
                  ))}
                  <option value="new">Add {person.label} as a new person</option>
                </Select>
              </Field>
            )}
          </div>
        )}
      </div>

      {canEdit && pending && (
        <div className="flex flex-wrap items-center gap-2 border-t border-neutral-200/70 px-4 py-2.5">
          <Button
            size="xs"
            variant="primary"
            onClick={apply}
            loading={busy}
            title={
              needsOrganization
                ? 'Choose or create the organization first'
                : needsDate
                  ? 'Set the date they first took the post first'
                  : undefined
            }
          >
            <Check className="h-3.5 w-3.5" strokeWidth={2} />
            {needsOrganization
              ? 'Choose organization'
              : needsDate
                ? 'Set the start date'
                : duplicate
                  ? 'Merge'
                  : 'Apply'}
          </Button>
          {!duplicate && (
            <Button size="xs" variant="secondary" onClick={() => setEditing((value) => !value)}>
              <Pencil className="h-3.5 w-3.5" strokeWidth={1.75} />
              {editing ? 'Done' : 'Change first'}
            </Button>
          )}
          {onVerify && (
            <Button size="xs" variant="ghost" onClick={onVerify} title="Research this one claim again, deeper">
              <ShieldCheck className="h-3.5 w-3.5" strokeWidth={1.75} />
              Check again
            </Button>
          )}
          <span className="flex-1" />
          <Button size="xs" variant="ghost" onClick={() => onDismiss()}>
            <X className="h-3.5 w-3.5" strokeWidth={1.75} />
            Dismiss
          </Button>
        </div>
      )}

      {update.status === 'dismissed' && (
        <p className="border-t border-neutral-200/70 px-4 py-2 text-[12px] text-neutral-400">
          Dismissed{update.dismissReason ? `: ${update.dismissReason}` : ' — it will not be proposed again for six months'}
        </p>
      )}
    </Card>
  );
};

/** The queue's own empty state, which depends on why it is empty */
export const UpdatesEmpty: React.FC<{ filtered: boolean; onResearch?: () => void }> = ({
  filtered,
  onResearch,
}) => (
  // One inner block: a Card rounds its last child's bottom corners, which squared off the button's pill
  <Card>
    <div className="px-4 py-10 text-center">
      <Sparkles className="mx-auto h-5 w-5 text-neutral-300" strokeWidth={1.5} />
      <p className="mt-2 text-[13px] text-neutral-600">{filtered ? 'Nothing here' : 'No changes waiting'}</p>
      <p className="mt-1 text-[12px] text-neutral-400">
        {filtered
          ? 'Try another filter'
          : 'Check who holds office today, and anything that has changed will appear here'}
      </p>
      {!filtered && onResearch && (
        <Button size="sm" variant="secondary" className="mt-3" onClick={onResearch}>
          Check office holders
        </Button>
      )}
    </div>
  </Card>
);
